import { useId } from 'react';

import { type PropertyBreakdown } from '../api/queries.js';

/**
 * A palette that stays visible against the dashboard's light background.
 *
 * The colours are intentionally distinct so neighbouring slices do not blur
 * together. They cycle for tenants with more than ten properties.
 */
const PALETTE: readonly string[] = [
  '#3b82f6',
  '#ef4444',
  '#22c55e',
  '#eab308',
  '#a855f7',
  '#f97316',
  '#14b8a6',
  '#ec4899',
  '#6366f1',
  '#06b6d4',
];

const VIEWBOX = '0 0 200 200';
const VIEWBOX_SIZE = 200;
const CENTER = 100;
const OUTER_RADIUS = 90;
const INNER_RADIUS = 50;
const SLICE_STROKE = 2;
const SHARE_TO_PERCENT = 100;

interface PolarPoint {
  readonly x: number;
  readonly y: number;
}

interface Slice {
  readonly row: PropertyBreakdown;
  readonly startAngle: number;
  readonly endAngle: number;
}

function pointAt(angle: number, radius: number): PolarPoint {
  return {
    x: CENTER + radius * Math.cos(angle),
    y: CENTER + radius * Math.sin(angle),
  };
}

function arcPath(
  startAngle: number,
  endAngle: number,
  outerRadius: number,
  innerRadius: number,
): string {
  const startOuter = pointAt(startAngle, outerRadius);
  const endOuter = pointAt(endAngle, outerRadius);
  const startInner = pointAt(startAngle, innerRadius);
  const endInner = pointAt(endAngle, innerRadius);

  const angleSpan = endAngle - startAngle;
  const largeArc = angleSpan > Math.PI ? 1 : 0;

  return [
    `M ${String(startOuter.x)} ${String(startOuter.y)}`,
    `A ${String(outerRadius)} ${String(outerRadius)} 0 ${String(largeArc)} 1 ${String(endOuter.x)} ${String(endOuter.y)}`,
    `L ${String(endInner.x)} ${String(endInner.y)}`,
    `A ${String(innerRadius)} ${String(innerRadius)} 0 ${String(largeArc)} 0 ${String(startInner.x)} ${String(startInner.y)}`,
    'Z',
  ].join(' ');
}

function buildSlices(data: readonly PropertyBreakdown[]): readonly Slice[] {
  const startAngle = 0;
  const fullCircle = Math.PI + Math.PI;
  const slices: Slice[] = [];
  let currentAngle = startAngle;

  for (const row of data) {
    const sliceAngle = row.share * fullCircle;
    const start = currentAngle;
    const end = currentAngle + sliceAngle;
    slices.push({ row, startAngle: start, endAngle: end });
    currentAngle = end;
  }

  return slices;
}

/**
 * A donut chart and colour legend for the property breakdown.
 *
 * Slices are described by a `<title>` so the browser shows a tooltip, and the
 * legend gives each property a readable name without forcing the user to hover.
 */
export function PropertyBreakdownChart({
  data,
}: {
  readonly data: readonly PropertyBreakdown[];
}): React.JSX.Element {
  const clipId = useId();

  if (data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
        No data to chart.
      </div>
    );
  }

  if (data.length === 1) {
    const only = data[0];
    if (only === undefined) {
      return (
        <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
          No data to chart.
        </div>
      );
    }

    const colour = PALETTE[0];
    if (colour === undefined) {
      return (
        <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
          No data to chart.
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <svg
          viewBox={VIEWBOX}
          className="h-52 w-52"
          role="img"
          aria-label={`${only.propertyName} makes up the entire value`}
        >
          <clipPath id={clipId}>
            <rect x="0" y="0" width={VIEWBOX_SIZE} height={VIEWBOX_SIZE} />
          </clipPath>
          <g fillRule="evenodd" clipPath={`url(#${clipId})`}>
            <circle cx={CENTER} cy={CENTER} r={OUTER_RADIUS} fill={colour} />
            <circle
              cx={CENTER}
              cy={CENTER}
              r={INNER_RADIUS}
              className="fill-background"
              style={{ fill: 'var(--background)' }}
            />
          </g>
          <title>
            {`${only.propertyName}: ${String(only.reservationCount)} reservations, ${only.totalAmountFormatted}`}
          </title>
        </svg>
        <ul className="space-y-1 text-sm">
          <li className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full" style={{ backgroundColor: colour }} />
            <span className="truncate">{only.propertyName}</span>
          </li>
        </ul>
      </div>
    );
  }

  const slices = buildSlices(data);

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <svg
        viewBox={VIEWBOX}
        className="h-52 w-52 shrink-0"
        role="img"
        aria-label="Property value share"
      >
        {slices.map(({ row, startAngle, endAngle }, index) => {
          const colour = PALETTE[index % PALETTE.length];
          if (colour === undefined) return null;
          const percentage = Math.round(row.share * SHARE_TO_PERCENT);

          return (
            <path
              key={row.propertyId}
              d={arcPath(startAngle, endAngle, OUTER_RADIUS, INNER_RADIUS)}
              fill={colour}
              stroke="var(--background)"
              strokeWidth={SLICE_STROKE}
            >
              <title>
                {`${row.propertyName}: ${String(row.reservationCount)} reservations, ${row.totalAmountFormatted} (${String(percentage)}%)`}
              </title>
            </path>
          );
        })}
      </svg>

      <ul className="max-h-52 space-y-1 overflow-y-auto pr-2 text-sm" aria-label="Property legend">
        {data.map((row, index) => {
          const colour = PALETTE[index % PALETTE.length];
          if (colour === undefined) return null;

          return (
            <li key={row.propertyId} className="flex items-center gap-2">
              <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: colour }} />
              <span className="truncate" title={row.propertyName}>
                {row.propertyName}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
